import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useOccurrence,
  useApproveOccurrence,
  useRejectOccurrence,
  newIdempotencyKey,
  familyKeys,
  resolveErrorMessage,
} from '@kidshome/api-contract';
import { Textarea } from '@kidshome/ui';
import { useAuthStore, useCan, personaFor } from '@/entities/session';
import {
  StatePill,
  LoadingState,
  ErrorState,
  PhotoProofViewer,
  CoinIcon,
  Avatar,
  MissionCover,
  useToast,
} from '@/shared/ui';
import { choreGlyph } from '@/shared/ui/glyphs';
import { ConceptIcon } from '@/shared/ui/ConceptIcon';
import { choreArt } from '@/assets/icons';
import { tDynamic, tPlayKey } from '@/shared/lib/i18n';
import { useHero, useHeroCurrencyName, HeroAvatar } from '@/entities/hero';
import { useHouseholdContext } from '@/shared/lib/household';
import { localDateISO, parseLocalDate } from '@/shared/lib/dates';
import { Perm } from '@/shared/lib/permission-codes';
import { useMembersGuarded } from '@/entities/member/use-members-guarded';
import { SkipTask } from '@/features/skip-task';
import { useCompleteTaskAction } from '@/features/complete-task';
import { useRedoTaskAction } from '@/features/redo-task';
import { BackfillTask } from '@/features/backfill-task';
import { PhotoCapture } from '@/features/upload-photo-proof';
import { NarratorBubble } from '@/widgets/narrator';
import { useBondReaction } from '@/widgets/bond-reaction';
import { GuardianProofPhoto } from './GuardianProofPhoto';
import './chore-detail.css';

/** Circumference of the mini bond ring (r=27 in the 62×62 viewBox) — drives the arc dash math. */
const RING_C = 2 * Math.PI * 27;

/**
 * Chore detail (hero-redesign kid screen 03). The warm chore "head" card (soft icon tile · title ·
 * gold reward + status), the meta rows (schedule · reward · approval), the optional photo-proof
 * affordance, and the actions: complete (optimistic on the occurrence + balance) and a kind "skip for
 * today" (no streak lost). When a kid checks off their OWN chore, the integration moment plays — the
 * princess "felt" the effort (a mini bond ring that ticks up, a floating "+N до зв'язку" reaction, the
 * bond-level meter, the form-track chip, and her authored voice line). A child only ever reaches their
 * own occurrences (API-scoped).
 */
export function ChoreDetailPage(): React.ReactElement {
  const { t } = useTranslation('family');
  // The hero-reaction beat strings live in the typed `play` catalog (stage/bond names); the rest of the
  // page stays in `family`. `tp` keeps the play namespace; `tPlayKey` strips any `play.` prefix.
  const { t: tp } = useTranslation('play');
  const { t: tCommon } = useTranslation('common');
  const { occurrenceId = '' } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  // Persona, resolved up-front because it gates the hero/cosmetics reads below. The integration-moment
  // beat only ever renders for the KID assignee, and `GET /play/hero` + `/cosmetics/equipped` resolve
  // their target from the principal — a GUARDIAN has no hero, so those reads would 403 ("Target must be
  // an active child of this household"). Gate the fetches on the kid persona so a reviewing guardian
  // never fires (or is denied) a hero/look read it doesn't use.
  const isKid = user ? personaFor(user) === 'kid' : false;
  const isGuardian = !isKid;
  const { can } = useCan();
  const { timeZone, locale } = useHouseholdContext();
  const toast = useToast();
  const qc = useQueryClient();

  const occ = useOccurrence(occurrenceId);

  // The guardian head + review surfaces resolve the assignee's name + avatar from the household roster
  // (the occurrence read-model carries only an `assigneeId`). The kid self-route never needs this (the
  // chore is their own), so the lookup stays decorative on the guardian path.
  const members = useMembersGuarded();

  // The integration-moment card shows the kid's ACTUAL chosen hero at her live form — same source the
  // /hero scene reads — so the face in the link matches the real hero (not a fabricated stage). It's
  // the viewer's own hero (this beat only ever renders for the kid assignee); null keeps the avatar
  // happy (neutral presence) before the query resolves. THE single hero state (ADR-0102).
  const { hero: heroData, effortRates } = useHero({ enabled: isKid });
  const currency = useHeroCurrencyName(heroData?.archetypeId ?? null);

  // The REAL bond reaction fires ONCE, transiently — but ONLY when the SERVER confirms the kid's own
  // completion HERE, never at tap time. The optimistic complete/redo flips the occurrence cache to
  // pending_approval/approved synchronously in onMutate (and rolls back on an offline/rejected write),
  // so deriving the celebration from the cache status would float a "+10" for a completion that never
  // landed — and would also fire for an externally-driven status change (a guardian completing it, a
  // background refetch). Mirroring the habits pattern, we instead track the id tapped-to-complete and
  // fire from the SETTLE: when the tracked id leaves the action's pending set with no error it confirmed
  // → the +10 is real. After it fires, the hero's ring/level advance on /hero via the live invalidation.
  const { reaction, fire: fireBond } = useBondReaction({ enabled: isKid });

  // Route completion through the shared optimistic action (same as TaskBoard) so it gets the instant
  // occurrence + balance bump and rollback, keyed on the occurrence's own day + assignee.
  const {
    complete,
    isPending,
    isOccurrencePending: isCompletePending,
    error: completeError,
  } = useCompleteTaskAction({
    date: occ.data?.scheduledLocalDate ?? '',
    assigneeId: occ.data?.assigneeId ?? '',
  });

  // "Try again" on a rejected chore re-submits it for approval (→ pending_approval); same optimistic
  // pattern as complete, keyed on the occurrence's own day + assignee.
  const {
    redo,
    isPending: isRedoPending,
    isOccurrencePending: isRedoOccPending,
    error: redoError,
  } = useRedoTaskAction({
    date: occ.data?.scheduledLocalDate ?? '',
    assigneeId: occ.data?.assigneeId ?? '',
  });

  // Guardian review (approve / return-for-redo) — the same writes the Inbox runs, now on the detail
  // page so a chore opened from the calendar can be signed off in place. Approve is `@Idempotent`
  // (carries a key); return requires the kind comment below. Both reconcile the queue + this
  // occurrence + the reviewed kid's balance/day/week so dashboards don't lag (mirrors ReviewOccurrence).
  const [reviewNote, setReviewNote] = useState('');
  // `chore/:occurrenceId` has no per-id `key`, so React Router doesn't remount this page on a
  // param-only navigation (one chore's detail link to another) — reset the note explicitly so it can
  // never leak onto a different occurrence.
  useEffect(() => {
    setReviewNote('');
  }, [occurrenceId]);
  const invalidateAfterReview = (
    reviewedOccurrenceId: string,
    assigneeId: string,
    scheduledLocalDate: string,
  ): void => {
    void qc.invalidateQueries({ queryKey: familyKeys.approvals() });
    void qc.invalidateQueries({ queryKey: familyKeys.occurrence(reviewedOccurrenceId) });
    void qc.invalidateQueries({ queryKey: familyKeys.balance(assigneeId) });
    void qc.invalidateQueries({ queryKey: familyKeys.day(assigneeId, scheduledLocalDate) });
    void qc.invalidateQueries({ queryKey: familyKeys.weeks(assigneeId) });
    // Reviewing an anytime mission (ADR-0141) changes the open Missions lane and (on approve) the
    // Finished shelf — both read under ['tasks','missions',assigneeId], which the day/week keys above
    // do NOT prefix-match. One call covers both: missionsFinished nests under the same prefix.
    void qc.invalidateQueries({ queryKey: familyKeys.missions(assigneeId) });
    // Approval is ALSO what advances the cooperative family-quest bar for approval-gated chores
    // (`task.occurrence-approved` → quest projection), so reconcile the shared quest card too — the
    // canonical Inbox review path does the same (see ReviewOccurrence). The projection lands via the
    // outbox poll (~5s), so this refetch plus refetch-on-focus keep the bar fresh without a timer.
    // Harmless on the reject path.
    void qc.invalidateQueries({ queryKey: familyKeys.questsActive() });
  };
  // Snapshot the reviewed occurrence's assignee + day in `onMutate` (which always runs synchronously
  // against the render that fired `.mutate()`), never read them from `occ.data`/the outer `occurrenceId`
  // inside `onSuccess`. `chore/:occurrenceId` has no per-id route `key` (see above), so a guardian can
  // navigate to a DIFFERENT chore's detail before this settles with no remount — and TanStack Query
  // rebinds a mutation's `onSuccess` to the LATEST render's closure while it's still pending
  // (`useMutation`'s effect calls `observer.setOptions` on every render; confirmed against the installed
  // `@tanstack/query-core`). Reading the live closures there would reconcile + navigate off whatever
  // occurrence the guardian has since scrolled to, not the one actually reviewed. `variables.occurrenceId`
  // (the mutation's own recorded variables) is immune to this — it's always the id this specific
  // approve/reject call was made with.
  const pinReviewContext = (): { assigneeId: string; scheduledLocalDate: string } | undefined => {
    const d = occ.data;
    return d ? { assigneeId: d.assigneeId, scheduledLocalDate: d.scheduledLocalDate } : undefined;
  };
  const approve = useApproveOccurrence<ReturnType<typeof pinReviewContext>>({
    onMutate: pinReviewContext,
    onSuccess: (_data, variables, context) => {
      toast.success(t('toast.choreApproved'));
      if (context) {
        invalidateAfterReview(
          variables.occurrenceId,
          context.assigneeId,
          context.scheduledLocalDate,
        );
      }
      void navigate(-1);
    },
    onError: (error) => {
      const m = resolveErrorMessage(error);
      toast.error(tDynamic(t, m.key, m.params));
    },
  });
  const reject = useRejectOccurrence<ReturnType<typeof pinReviewContext>>({
    onMutate: pinReviewContext,
    onSuccess: (_data, variables, context) => {
      toast.success(t('toast.choreSentBack'));
      setReviewNote('');
      if (context) {
        invalidateAfterReview(
          variables.occurrenceId,
          context.assigneeId,
          context.scheduledLocalDate,
        );
      }
      void navigate(-1);
    },
    onError: (error) => {
      const m = resolveErrorMessage(error);
      toast.error(tDynamic(t, m.key, m.params));
    },
  });

  // Settle-based celebration: track when THIS occurrence is mid-complete/redo (the only writes that
  // award chore bond), then fire the reaction + reveal the celebrate beat once it leaves the pending set
  // with no error. An externally-driven status change never enters this set, so it never fires the kid's
  // "+10" moment; an offline/rejected write rolls the cache back and (carrying an error) drops it. The
  // confirmed flag is STATE (not the cache status) so the celebrate beat is owned by THIS kid's action.
  const [confirmedHere, setConfirmedHere] = useState(false);
  const wasPending = useRef(false);
  useEffect(() => {
    setConfirmedHere(false);
    wasPending.current = false;
  }, [occurrenceId]);
  const occWritePending = isCompletePending(occurrenceId) || isRedoOccPending(occurrenceId);
  const writeError = completeError ?? redoError;
  if (occWritePending) wasPending.current = true;
  useEffect(() => {
    if (occWritePending) return; // still in flight — wait for the settle
    if (!wasPending.current) return; // nothing of ours was in flight
    wasPending.current = false;
    if (writeError == null) {
      // Confirmed by the server → the +10 is real: float the transient reaction + show the beat.
      fireBond('chore');
      setConfirmedHere(true);
    }
  }, [occWritePending, writeError, fireBond]);

  if (occ.isPending) return <LoadingState />;
  if (occ.isError) return <ErrorState onRetry={() => void occ.refetch()} />;

  const data = occ.data;
  const isOpen = data.status === 'planned';
  const isRejected = data.status === 'rejected';
  // A FORGOTTEN day: the backend sweeps a chore whose local day has ended to `missed`, so that status
  // IS the honest "this day is over and was forgotten" signal. It's never too late to log it for its
  // real day (ADR-0040) — we offer a gentle "log it for {{day}}" instead of a dead "MISSED". Only
  // within ~the last week (the server owns the hard bound + rejects an over-window backfill kindly).
  // Back-dating is a GUARDIAN action (ADR-0053): a child acts on TODAY only and sees a past day
  // read-only, so the affordance is gated on a guardian capability (approve), not the child's complete.
  const today = localDateISO(timeZone);
  // Backfill is a GUARDIAN action (a child can never back-date). Gate it on the SERVER's actual
  // requirement — the route requires `occurrence:complete` (not `occurrence:approve`) and the handler
  // rejects a child actor — so a custom-role guardian holding one code but not the other is neither
  // wrongly shown a 403-bound button nor wrongly denied an allowed one (mirrors DayFill's gate). A
  // guardian is UNBOUNDED on the past side (`isWithinBackfillWindow(..., guardianUnbounded: true)`), so
  // any past `missed` day is loggable with no client window cap — the server still validates.
  const canBackfill = isGuardian && can(Perm.OccurrenceComplete) && data.status === 'missed';
  // A CHILD acts on TODAY only (ADR-0083): they reach this page for any of their own days (the kid
  // Home is a navigable day-calendar), but checking off / skipping / redoing a past or future chore is
  // hidden — past + future are read-only for a kid. A guardian is unrestricted (they act on any day,
  // plus the backfill window above). The server enforces the same today-only rule for complete/skip/redo,
  // EXCEPT an open-ended mission, which a kid may COMPLETE on any day (see `editableForKid`).
  // (`isKid`/`isGuardian` are resolved up-front — they gate the hero/cosmetics reads above.)
  const isToday = data.scheduledLocalDate === today;
  // The assignee's roster row drives the guardian-only identity line in the head + the photo
  // attribution. Decorative — a still-loading or unknown member just omits the row.
  const assignee = (members.data?.data ?? []).find((m) => m.id === data.assigneeId);
  // "вівторок, 23 червня" — the scheduled local day rendered in the household locale (weekday + day +
  // month), the same Intl convention the habit calendar uses; pinned to the household timezone.
  const scheduledDayLabel = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(parseLocalDate(data.scheduledLocalDate));
  // The photo attribution tag reads "Додала {name} о 14:18". `completedAt` is when the proof was
  // submitted (per the contract). The mockup shows the time ONLY when it's available — a guardian
  // reviewing a freshly-synced proof with no timestamp still gets the name. Pinned to the household zone.
  const submittedTimeLabel =
    data.completedAt != null
      ? new Intl.DateTimeFormat(locale, {
          hour: '2-digit',
          minute: '2-digit',
          timeZone,
        }).format(new Date(data.completedAt))
      : null;
  // The guardian review bar appears only on a chore awaiting sign-off (pending_approval) for a guardian
  // who may approve. Returning needs the kind comment; approving is one tap.
  const canReview = isGuardian && can(Perm.OccurrenceApprove) && data.status === 'pending_approval';
  // The photo-proof reveal (the child's photo + name + submission time, below) is gated on its OWN
  // per-domain read code, `chore:photo:read` — the sensitive tier withheld from the Relative preset
  // (permission-codes.ts) so a restricted co-guardian can't browse a child's proof images, even though
  // the broad, non-instance-scoped occurrence read (see the upload-endpoint note below) lets them load
  // this occurrence at all. This is deliberately NOT `Perm.OccurrenceApprove` (`canReview`'s code):
  // ADR-0024 roles are independent permission bundles, so a role can hold one without the other (e.g. a
  // custom role granted `occurrence:approve` but not `chore:photo:read` must still never see the photo
  // — mirroring the server's own `GET /tasks/photo-proof/:key`, which requires `chore:photo:read`, not
  // `occurrence:approve`). Also unlike `canReview`, this is NOT status-scoped: a guardian who CAN view
  // photos should still see it after the chore's approved/rejected (it's the audit trail), not only
  // while pending_approval.
  const canViewProofPhoto = isGuardian && can(Perm.ChorePhotoRead);
  const reviewPending = approve.isPending || reject.isPending;
  // A kid may complete only on TODAY — EXCEPT an open-ended "mission" (ADR-0141), which has no calendar
  // day and so is completable on any day it's open (the server's complete handler skips the today-only
  // bound for `openEnded`). A guardian is unrestricted. Without the `openEnded` clause the detail page
  // would show a mission read-only on a non-today day while the Missions lane (and server) both allow it.
  const editableForKid = !isKid || isToday || data.openEnded;
  // A rejected chore can be re-submitted by the assignee (or a guardian who could complete it). The
  // domain re-completes from `rejected` → `pending_approval`; `complete()` is guarded to `planned`.
  // The server's REDO handler enforces the child TODAY-only bound (no open-ended exemption), so a kid's
  // redo is today-gated too (a guardian redoes any day) — offering it on a past day would only 403 with
  // CHORE_ACT_ON_TODAY_ONLY and roll back. Matches the kid day board (its redo control is `canComplete`-
  // gated, i.e. today-only) — this page was the one surface that let a kid redo a past day.
  const canRedo = can(Perm.OccurrenceComplete) && isRejected && (!isKid || isToday);
  const redoErrorMessage =
    redoError != null
      ? (() => {
          const m = resolveErrorMessage(redoError);
          return tDynamic(t, m.key, m.params);
        })()
      : null;
  const canComplete = can(Perm.OccurrenceComplete) && isOpen && editableForKid;
  // Skip is its OWN capability (`occurrence:skip`) — the server's skip route requires it, NOT
  // `occurrence:complete`, so gate it independently (a custom role may hold skip without complete). Same
  // status/day/mission bounds the server enforces for skip: only an open, planned chore; kids today-only
  // (guardians any day, via `editableForKid`); never an open-ended mission (a child can't abandon one).
  const canSkip = !!user && can(Perm.OccurrenceSkip) && isOpen && editableForKid && !data.openEnded;
  // A kid looking at a planned chore on another day sees it read-only with a kind reason (no dead
  // disabled button) — the work happens on its real day. NOT for an open-ended mission: it has no fixed
  // day and is completable now (so it shows the live Mark-done above, never this "come back" hint).
  const showNotTodayHint = isKid && !isToday && isOpen && !data.openEnded;
  const isAssignee = user?.id === data.assigneeId;
  // Photo proof is required only from the ASSIGNEE (the kid). A guardian completing/redoing on the
  // child's behalf is AUTHORITATIVE and photo-EXEMPT: the domain skips `PhotoProofRequiredError` for a
  // guardian actor (`TaskOccurrence.complete()`/`.redo()` `guardianActing`), and the guardian calendar's
  // DayFill already offers the same one-tap, camera-free completion. So a guardian falls through to the
  // plain "Mark done" button below (never forced through a needless capture the server doesn't require);
  // only the kid's own completion runs THROUGH the capture flow (upload → complete/redo with the key).
  // A rejected photo chore the KID re-submits likewise needs a fresh capture (rejection purged the old
  // photo); a guardian's redo stays photo-exempt. The render below is already written for this
  // (assignee-only) meaning — the definition had merely dropped the `isAssignee` guard.
  const needsPhoto = data.requiresPhotoProof && isAssignee;
  const redoNeedsPhoto = canRedo && needsPhoto;
  // A REJECTED photo chore's stored `photoProofKey` is DANGLING: rejection purges the photo BYTES
  // (`RejectOccurrenceHandler`) but can leave the column pointing at the deleted object, so fetching it
  // 404s. Treat it as absent for ANY rejected photo chore — keyed on the rejected status, NOT on
  // `redoNeedsPhoto`, because a guardian's redo is photo-exempt (so `redoNeedsPhoto` is false for them,
  // yet the stored key is just as dangling). This keeps every viewer below (the guardian's read-only
  // card, the assignee's own thumbnail) off a dead image; the next submission re-establishes a real key.
  const currentPhotoProofKey = isRejected && data.requiresPhotoProof ? null : data.photoProofKey;
  // Surface a completion failure to the child (don't fail silently after the optimistic rollback).
  const completeErrorMessage =
    completeError != null
      ? (() => {
          const m = resolveErrorMessage(completeError);
          return tDynamic(t, m.key, m.params);
        })()
      : null;
  // The integration moment plays only for the kid assignee on a chore THIS kid just confirmed-done on
  // this screen (server-settled, no error — see `confirmedHere` above), never on a status the cache
  // merely reflects (an already-done revisit, or an externally-driven completion). The chore is genuinely
  // done now too (the optimistic flip reconciled), so the done-banner copy can read off `requiresApproval`.
  const showReaction = confirmedHere && isKid && isAssignee;
  // Live bond meter from the kid's ACTUAL hero — same query the /hero scene reads. `bondLevel(bond)`
  // derives the endless level + ring fill purely from the stored monotonic bond (no fabricated numbers).
  const bl = heroData?.level ?? null;
  // The SERVED per-chore award (never a local mirror) — the same +N the completion actually earned.
  const choreBond = effortRates.chore;
  const tp_ = (key: string, params?: Record<string, unknown>): string => tPlayKey(tp, key, params);

  return (
    <div
      className={
        // Guardian review (family theme) drops the kid-page width/cozy layer so the warm-neutral theme
        // applies cleanly; the kid assignee view keeps its cozy kid-page.
        isGuardian
          ? 'chore-detail-page flex flex-col gap-3'
          : 'chore-detail-page kid-page flex flex-col gap-3'
      }
    >
      {/* A quiet back affordance — a chevron + label, low-emphasis (mirrors the mockup's header). */}
      <button
        type="button"
        onClick={() => void navigate(-1)}
        aria-label={t('common.back')}
        className="back-link self-start"
      >
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M15 6l-6 6 6 6"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {t('common.back')}
      </button>

      {/* The warm chore "head" card: a soft icon tile · title · gold coin reward + status pill. */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="chore-head">
          {/* The guardian's cover photo (a mission's book cover, etc.) fills the head tile when present —
              the same image the kid saw on the Missions shelf — else the soft chore-icon glyph. */}
          <span
            className="chore-icon"
            style={data.coverPhotoKey ? { padding: 0, overflow: 'hidden' } : undefined}
            aria-hidden="true"
          >
            {data.coverPhotoKey ? (
              <MissionCover
                icon={data.icon}
                coverPhotoKey={data.coverPhotoKey}
                className="size-full"
                rounded="rounded-[inherit]"
                glyphSize={38}
              />
            ) : (
              <ConceptIcon art={choreArt(data.icon)} glyph={choreGlyph(data.icon)} size={36} />
            )}
          </span>
          <h1 className="chore-title">{data.title}</h1>
          <div className="row" style={{ gap: 8 }}>
            {data.coinValue > 0 && (
              <span
                className="chore-coin"
                role="img"
                aria-label={t('choreDetail.rewardOnDone', {
                  coins: data.coinValue,
                })}
              >
                <CoinIcon />+{data.coinValue}
              </span>
            )}
            <StatePill status={data.status} />
          </div>
          {/* Guardian identity line — avatar · child name · the scheduled day (the mockup's
              "Софійка · вівторок, 23 червня"). The kid never needs it (it's their own chore). A guardian
              whose role lacks `member:read` gets an honest inline note instead of a silently blank line
              (ADR-0137) — the rest of the page (review/complete actions) still works without the roster.
              A full PermissionDeniedState block would dwarf this single identity line, so it's plain
              text here, matching the row's own size. */}
          {isGuardian && members.forbidden ? (
            <span className="text-sm text-text-muted">{tCommon('states.forbiddenTitle')}</span>
          ) : (
            isGuardian &&
            assignee && (
              <div className="flex items-center gap-2 text-sm text-text-muted">
                <Avatar avatar={assignee.avatar} size="sm" className="size-6 text-xs shadow-none" />
                <span>
                  {assignee.displayName} · {scheduledDayLabel}
                </span>
              </div>
            )
          )}
        </div>
      </div>

      {/* ФОТО-ПІДТВЕРДЖЕННЯ — the guardian review presentation (mockup 11). It sits directly under the
          head card (before the meta rows), matching the mockup's section order: head → photo → meta →
          comment → savebar. Read-only: a guardian never captures the proof. Gated on
          `currentPhotoProofKey` (not the raw `photoProofKey`) so a rejected chore's dangling key never
          renders this read-only frame — that case falls through to the capture card below instead. */}
      {data.requiresPhotoProof && canViewProofPhoto && currentPhotoProofKey != null && (
        <GuardianProofPhoto
          photoKey={currentPhotoProofKey}
          choreTitle={data.title}
          submitterName={assignee?.displayName ?? null}
          submittedTimeLabel={submittedTimeLabel}
        />
      )}

      {/* THE INTEGRATION MOMENT — a brief celebratory beat right after the kid checks off their own
          chore: the hero "felt" the effort. Shown only to the kid assignee once THIS kid's completion is
          server-confirmed here (`confirmedHere`), never derived from the cache status. */}
      {showReaction && (
        <>
          {/* The chore now reads done — a warm "Готово! Чекає дорослого 🛡️" banner (the lead + shield
              from the mockup), or just "Готово!" when it auto-approves. role=status owns the live
              announcement (the celebrate card below is a plain link, not a status). */}
          <div className="done-banner" role="status">
            <span className="chk" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path
                  d="m5 12 4.5 4.5L19 7"
                  stroke="currentColor"
                  strokeWidth="2.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            {data.requiresApproval ? t('choreDetail.doneAwaiting') : t('choreDetail.done')}
          </div>

          {/* The REAL bond gain: a transient "+N до зв'язку" reaction fires at the SETTLE of completion
              (see the effect above), then fades. The card is a plain LINK to the Hero tab (the live bond
              ring/level advance there) — it keeps its button semantics (no role=status, which would hide
              the action from a screen reader); the done-banner above owns the live announcement. The face
              + meter + form chip below all read the kid's ACTUAL hero at her live form (no fabricated
              numbers). */}
          <button
            type="button"
            className="react-card"
            onClick={() => navigate('/hero')}
            aria-label={tp_('hero.celebrate.levelTitle', {
              n: bl?.value ?? 1,
            })}
            style={{ position: 'relative' }}
          >
            {reaction && (
              <span
                style={{
                  position: 'absolute',
                  top: -6,
                  insetInlineEnd: 14,
                  zIndex: 3,
                }}
              >
                {reaction}
              </span>
            )}
            <div className="react-top">
              {/* mini bond ring with the kid's live hero face; the arc fills to her real bond fraction
                  (stilled under reduced-motion via the co-located CSS) */}
              <div className="react-ring" aria-hidden="true">
                <svg className="ring-svg" viewBox="0 0 62 62" fill="none">
                  <circle cx="31" cy="31" r="27" stroke="rgba(116,88,181,.16)" strokeWidth="5" />
                  <circle
                    className="tick"
                    cx="31"
                    cy="31"
                    r="27"
                    stroke="#FFB996"
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeDasharray={RING_C}
                    strokeDashoffset={RING_C * (1 - (bl?.fraction ?? 0))}
                    transform="rotate(-90 31 31)"
                  />
                </svg>
                <HeroAvatar
                  archetypeId={heroData?.degraded ? null : (heroData?.archetypeId ?? null)}
                  stageIndex={heroData?.stageIndex ?? 0}
                  look={heroData?.degraded ? undefined : heroData?.look}
                  size={40}
                  className="face"
                />
              </div>
              <div className="react-head">
                {/* "felt your effort" framing via the real bond-level headline + the explicit +N delta */}
                <h3>{tp_('hero.celebrate.levelTitle', { n: bl?.value ?? 1 })}</h3>
                <p>{tp_('hero.level.reaction', { n: choreBond, currency })}</p>
              </div>
              <span className="chev" aria-hidden="true" style={{ width: 18, height: 18 }}>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M9 6l6 6-6 6"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>

            {/* DUAL-TRACK line: the ENDLESS bond-level meter — real level + ring fill from bondLevel(bond) */}
            {bl && (
              <div className="react-meter-wrap">
                <div className="react-meter-top">
                  <span className="lvl">{tp_('hero.level.label', { n: bl.value })}</span>
                  <span className="lvl muted">
                    {bl.atMax
                      ? tp_('hero.level.max', { currency })
                      : tp_('hero.level.next', { n: bl.starsToNext ?? 0, currency })}
                  </span>
                </div>
                <div className="meter">
                  <div
                    className="meter-fill gold"
                    style={{ width: `${Math.round(bl.fraction * 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* DUAL-TRACK line: the finite FORM-track chip — the kid's current stage name. (The
                "next form" pill was dropped: it pre-announced a far-off stage that read as a goalpost
                more than a celebration on this just-finished-a-chore card.) */}
            {heroData && !heroData.degraded && (
              <div className="form-line">
                <span className="chip">
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M5 9l3-5 4 6 4-6 3 5-1.5 8h-11z" fill="#E0A92E" />
                  </svg>
                  {tp_(heroData.stage.nameKey)}
                </span>
              </div>
            )}
          </button>

          {/* the hero's authored, kind voice line — never absence-triggered, no FOMO. Pass the kid's
              active archetype so the idle fallback speaks in THEIR hero's voice (a prince sounds
              masculine, not the generic feminine greeting); a degraded hero → gender-safe neutral. */}
          <NarratorBubble
            archetypeId={heroData?.degraded ? null : (heroData?.archetypeId ?? null)}
          />
        </>
      )}

      {/* On a rejected chore, surface the grown-up's review note (otherwise dropped) as a kind banner
          so the kid knows WHAT to change — never shaming, no raw codes. */}
      {isRejected && (
        <div
          className="card"
          role="note"
          style={{
            background: 'var(--warning-fg)',
            border: '1px solid var(--warning)',
          }}
        >
          <p className="text-sm font-bold" style={{ color: 'var(--ink)' }}>
            {t('choreDetail.reviewNoteTitle')}
          </p>
          <p className="mt-1 text-sm" style={{ color: 'var(--ink)' }}>
            {data.reviewNote ?? t('choreDetail.tryAgainHint')}
          </p>
        </div>
      )}

      {/* A photo-proof chore re-submits through the capture below (fresh photo → redo with the new
          key), so the bare "Try again" button only appears for plain chores. */}
      {canRedo && !redoNeedsPhoto && (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            className="btn btn-ghost btn-block"
            disabled={isRedoPending}
            onClick={() => redo(occurrenceId)}
          >
            {t('choreDetail.tryAgain')}
          </button>
          {redoErrorMessage && (
            <p role="alert" className="text-center text-sm text-danger">
              {redoErrorMessage}
            </p>
          )}
        </div>
      )}

      {/* Meta rows — schedule · reward · approval — each with a soft colored icon tile and a hairline
          between rows (one warm card, not three flat ones). */}
      <div className="card" style={{ padding: '4px 14px' }}>
        <div className="meta-row">
          <span className="lbl">
            <span className="meta-ic ic-mint" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="13" r="8" stroke="currentColor" strokeWidth="2" />
                <path
                  d="M12 9.5V13l2.4 1.6M12 3.5v2"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            {t('choreDetail.schedule')}
          </span>
          <span className="val">{t(`timeOfDay.${data.timeOfDay}`)}</span>
        </div>
        {/* The reward row is gated on a real coin value — a no-coin chore (just helping) never shows
            "0 за виконання", matching the head tag's `coinValue > 0` logic so the two coin surfaces agree. */}
        {data.coinValue > 0 && (
          <div className="meta-row">
            <span className="lbl">
              <span className="meta-ic ic-gold" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="2" />
                  <path
                    d="M12 7.5v9M9.6 10h4a1.6 1.6 0 0 1 0 3.2h-4"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              {t('choreDetail.reward')}
            </span>
            <span className="val">{t('choreDetail.rewardOnDone', { coins: data.coinValue })}</span>
          </div>
        )}
        <div className="meta-row">
          <span className="lbl" style={{ minWidth: 0 }}>
            <span className="meta-ic ic-brand" aria-hidden="true">
              {data.requiresApproval ? (
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 3l7 3v5c0 4.5-3 7.7-7 9-4-1.3-7-4.5-7-9V6z" fill="currentColor" />
                  <path
                    d="m9 12 2 2 4-4"
                    stroke="#fff"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M13 3 5 13h5l-1 8 8-10h-5z" fill="currentColor" />
                </svg>
              )}
            </span>
            {/* The guardian review screen uses the compact mockup label ("Схвалення"); the kid keeps the
                expressive copy that explains what approval means for them. */}
            {isGuardian
              ? t('choreDetail.approvalLabel')
              : data.requiresApproval
                ? t('choreDetail.approvalTitleOn')
                : t('choreDetail.approvalTitleOff')}
          </span>
          <span className="val" style={{ maxWidth: 150, textAlign: 'end' }}>
            {/* Guardian: the compact "Потрібне · з фото" / "Потрібне" / "Не потрібне" (mockup). Kid: the
                full explanatory sentence. */}
            {isGuardian
              ? data.requiresApproval
                ? data.requiresPhotoProof
                  ? t('choreDetail.approvalWithPhoto')
                  : t('choreDetail.approvalNeeded')
                : t('choreDetail.approvalNotNeeded')
              : data.requiresApproval
                ? t('choreDetail.approvalOn')
                : t('choreDetail.approvalOff')}
          </span>
        </div>
      </div>

      {/* The kid/assignee photo-proof card: for an open or redo-needing chore the capture IS the
          completion path; otherwise it shows the submitted / needed hint and (when present) the viewer
          thumbnail. The guardian's read-only review uses the framed photo above instead — but only when
          there's a CURRENT photo to show (`currentPhotoProofKey`); a guardian redoing a rejected chore
          with a dangling key falls through to this card too, same as the assignee. */}
      {data.requiresPhotoProof && !(isGuardian && currentPhotoProofKey != null) && (
        <div className="card stack" style={{ gap: 12 }}>
          <div className="spread" style={{ gap: 12 }}>
            <div className="row" style={{ gap: 11, minWidth: 0 }}>
              <span
                className="flex size-[34px] flex-none items-center justify-center"
                style={{
                  borderRadius: 9,
                  background: 'var(--surface-muted)',
                  color: 'var(--lavender)',
                }}
                aria-hidden="true"
              >
                <svg viewBox="0 0 24 24" width="19" height="19" fill="none">
                  <rect
                    x="3"
                    y="7"
                    width="18"
                    height="13"
                    rx="3"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <circle cx="12" cy="13.5" r="3.3" stroke="currentColor" strokeWidth="2" />
                  <path
                    d="M8.5 7l1.2-2h4.6L15.5 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <div className="stack" style={{ minWidth: 0 }}>
                <p className="font-bold" style={{ color: 'var(--ink)' }}>
                  {t('choreDetail.photoTitle')}
                </p>
                <p className="text-xs font-semibold" style={{ color: 'var(--muted)' }}>
                  {isOpen || redoNeedsPhoto
                    ? t('photoProof.captureHint')
                    : data.photoProofKey != null
                      ? t('photoProof.submittedHint')
                      : t('choreDetail.photoNeeded')}
                </p>
              </div>
            </div>
            {currentPhotoProofKey != null && (
              <PhotoProofViewer photoKey={currentPhotoProofKey} choreTitle={data.title} />
            )}
          </div>
          {/* The capture IS the completion path for a photo-proof chore: pick/take a photo → upload →
                complete with the returned key (the chore then moves to pending_approval). On a REJECTED
                photo chore the same capture re-submits via redo with the FRESH key — the old photo was
                purged on rejection, so a guardian always reviews a real, current photo. Gated on
                `needsPhoto` (assignee-only) too — `canComplete` alone says nothing about WHO must supply
                the photo, so a guardian reviewing someone else's not-yet-submitted chore must fall
                through to the plain "Mark done" button below instead of being offered this capture. */}
          {((canComplete && needsPhoto) || redoNeedsPhoto) && (
            <PhotoCapture
              occurrenceId={occurrenceId}
              onUploaded={(photoProofKey) =>
                redoNeedsPhoto ? redo(occurrenceId, photoProofKey) : complete(data, photoProofKey)
              }
            />
          )}
          {redoNeedsPhoto && redoErrorMessage && (
            <p role="alert" className="text-center text-sm text-danger">
              {redoErrorMessage}
            </p>
          )}
        </div>
      )}

      {(canComplete || canSkip) && (
        <div className="flex flex-col gap-2">
          {/* A plain (non-photo) chore keeps its direct, big "Mark done" action. */}
          {canComplete && !needsPhoto && (
            <button
              type="button"
              className="btn btn-primary btn-block"
              disabled={isPending}
              onClick={() => complete(data)}
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="m5 12 4.5 4.5L19 7"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t('choreDetail.markDone')}
            </button>
          )}
          {canComplete && completeErrorMessage && (
            <p role="alert" className="text-center text-sm text-danger">
              {completeErrorMessage}
            </p>
          )}
          {/* Gentle "skip for today" link — no streak lost, never a loud button (the feature renders
              its own kind copy + confirm sheet). Gated on its OWN `occurrence:skip` capability
              (`canSkip`), independent of `occurrence:complete`, mirroring the server's skip route. */}
          {canSkip && (
            <SkipTask
              occurrenceId={occurrenceId}
              templateId={data.templateId}
              date={data.scheduledLocalDate}
              assigneeId={data.assigneeId}
              onSkipped={() => void navigate(-1)}
            />
          )}
        </div>
      )}

      {/* A kid viewing a planned chore on another day (their Home is a navigable day-calendar) sees a
          calm reason instead of an action — the work happens on its real day, never early/late. */}
      {showNotTodayHint && (
        <div className="card card-muted text-center" role="note">
          <p className="text-sm font-semibold" style={{ color: 'var(--muted)' }}>
            {t('today.notTodayHint')}
          </p>
        </div>
      )}

      {/* A FORGOTTEN day (missed, or planned but already past) gets the gentle "Forgot to check this
          off? Log it for {{day}}" affordance instead of a dead state — never shaming. The sheet runs
          the photo capture first when the chore needs proof, then backfills for its real day. */}
      {canBackfill && <BackfillTask occurrence={data} onDone={() => void occ.refetch()} />}

      {/* Guardian review (only on a pending_approval chore) — the kind return note + the sticky
          savebar with "Return" (ghost) + "Approve" (primary), mirroring the mockup. Returning carries
          the comment; approving is one tap. The Inbox runs the same writes; here a chore opened from
          the calendar is signed off in place. */}
      {canReview && (
        <>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="chore-review-note" className="text-sm font-semibold text-text">
              {t('choreDetail.reviewNoteLabel')}
            </label>
            <Textarea
              id="chore-review-note"
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder={t('approvals.rejectPlaceholder')}
            />
          </div>

          <div className="sticky bottom-0 -mx-4 flex gap-2 border-t border-border bg-surface px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3">
            <button
              type="button"
              className="btn btn-ghost flex-1"
              disabled={reviewPending || reviewNote.trim().length === 0}
              onClick={() =>
                reject.mutate({
                  occurrenceId,
                  note: reviewNote.trim(),
                  idempotencyKey: newIdempotencyKey(),
                })
              }
            >
              {t('approvals.needsChanges')}
            </button>
            <button
              type="button"
              className="btn btn-primary flex-1"
              disabled={reviewPending}
              onClick={() =>
                approve.mutate({
                  occurrenceId,
                  idempotencyKey: newIdempotencyKey(),
                })
              }
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="m5 12 4.5 4.5L19 7"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t('approvals.approve')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
